import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import "./components/app/css/NewAppCommon.css";
import "./App.css";
import Application from "./components/app/Application";
import Main from "./components/main/Main";
import VerifyConsent from "./components/app/verification/VerifyConsent"; // Import VerifyConsent component
import VerifyEmail from "./components/app/verification/VerifyEmail"; // Import VerifyEmail component
import VerifyEmailChange from "./components/app/verification/VerifyEmailChange"; // Import VerifyEmailChange component
import VerifyPhone from "./components/app/verification/VerifyPhone"; // Import VerifyPhone component
import ResetPassword from "./components/app/usermgmt/ResetPassword"; // Import ResetPassword component
import SetPasswordPage from "./components/app/usermgmt/SetPasswordPage"; // Import SetPasswordPage component
// UserAuthAPI and UserAPI session validation now handled inside Application component
// import UserAuthAPI from "./components/app/services/UserAuthAPI";
// import UserAPI from "./components/app/services/UserAPI";
import CSRFService from "./components/app/services/CSRFService"; // Import CSRF service
// Auth utilities moved to Application component
// import { clearFrontendAuthData, hasFrontendAuthData } from "./components/app/utils/AuthUtil";
// import { setCookieMinutes } from "./components/app/utils/CookieUtil";
import TermsAndConditions from "./components/legal/TermsAndConditions"; // Import TermsAndConditions component
import PrivacyPolicy from "./components/legal/PrivacyPolicy"; // Import PrivacyPolicy component
import UserConsentAgreement from "./components/legal/UserConsentAgreement"; // Import UserConsentAgreement component
import Disclosures from "./components/legal/Disclosures"; // Import Disclosures component
import SmsConsent from "./components/legal/SmsConsent"; // Import SmsConsent component
import Blogs from "./components/app/blogs/Blogs"; // Import Blogs component (for individual blog posts)
import MoreBlogs from "./components/app/blogs/MoreBlogs"; // Import MoreBlogs component (for blog listing)
import SimulationLanding from "./components/app/simulation/SimulationLanding"; // Import SimulationLanding component
import SimulationLandingSMS from "./components/app/simulation/SimulationLandingSMS"; // Import SimulationLandingSMS component
import DemoRegister from "./components/demo/DemoRegister"; // Import DemoRegister component
import EventList from "./components/events/EventList"; // Public events index
import EventDetail from "./components/events/EventDetail"; // Public event detail
import EventRegister from "./components/events/EventRegister"; // Public event registration
import EventConfirmation from "./components/events/EventConfirmation"; // Ticket / confirmation
import EventManage from "./components/events/EventManage"; // Login-free registration management
import EventRecap from "./components/events/EventRecap"; // Post-event materials
import HostRequest from "./components/events/HostRequest"; // "Invite us to speak" lead form
import { ErrorProvider } from "./components/app/context/ErrorContext"; // Import ErrorProvider for global error handling
import { LoadingProvider } from "./contexts/LoadingContext"; // Import LoadingProvider for global loading state
import { NavigationProvider } from "./components/app/context/NavigationContext"; // Import NavigationProvider for navigation service
import { UserProvider, useUser } from "./components/app/context/UserContext"; // Import UserProvider and useUser hook
import { SubscriptionProvider } from "./components/app/context/SubscriptionContext"; // Import SubscriptionProvider for subscription management
import { EditionProvider } from "./components/app/context/EditionContext"; // Edition-awareness (consumer vs corporate) for terminology + plan visibility
import { AccountSetupProvider } from "./components/app/context/AccountSetupContext"; // Import AccountSetupProvider for account setup tracking
import UpdateNotification from "./components/app/UpdateNotification"; // PWA update banner
import { LanguageProvider } from "./i18n/LanguageContext"; // i18n provider for English/Spanish UI

// Check if the current hostname is the app subdomain
const isAppDomain = () => window.location.hostname === "app.kinprotect.com";

function AppInner() {
  const navigate = useNavigate();
  const { loggedInUser } = useUser(); // Access user context

  // Redirect root path to /app when accessed via app.kinprotect.com
  useEffect(() => {
    if (isAppDomain() && window.location.pathname === "/") {
      navigate("/app", { replace: true });
    }
  }, [navigate]);

  // Fetch CSRF token on app initialization (skip for public pages)
  useEffect(() => {
    const initializeCsrfProtection = async () => {
      // Skip CSRF token fetch for public pages that don't need authentication
      const publicPaths = [
        "/sim-sms/", // SMS simulation landing pages
        "/sim-email", // Email simulation landing page
        "/verify-email-change",
        "/reset-password",
        "/set-password",
        "/terms-and-conditions",
        "/privacy-policy",
        "/user-consent-agreement",
        "/disclosures",
        "/sms-consent",
        "/blogs",
        "/more-blogs",
        "/demo/register",
        "/events", // Public event pages - anonymous visitors, no session
      ];

      const currentPath = window.location.pathname;
      const isPublicPage = publicPaths.some((path) =>
        currentPath.startsWith(path),
      );

      if (isPublicPage) {
        console.log(
          "⏭️ App: Skipping CSRF token fetch for public page:",
          currentPath,
        );
        return;
      }

      console.log("🔒 App: Initializing CSRF protection...");
      const success = await CSRFService.fetchCsrfToken();
      if (success) {
        console.log("✅ App: CSRF protection initialized successfully");
      } else {
        console.warn("⚠️ App: Failed to initialize CSRF protection");
      }
    };

    initializeCsrfProtection();
  }, []); // Run once on mount

  const handleHomeClick = () => {
    console.log("🏠 App: handleHomeClick called - navigating back to Main");
    if (isAppDomain()) {
      // On app subdomain, redirect to the marketing site
      window.location.href = "https://shield.kinprotect.com";
    } else {
      navigate("/"); // Navigate to the main marketing site route
    }
  };

  return (
    <div className="App">
      {" "}
      <Routes>
        {/* Route for consent verification */}
        <Route
          path="/verify-consent/:verificationKey"
          element={<VerifyConsent />}
        />

        {/* Route for email verification */}
        <Route
          path="/api/verify-email/:verificationKey"
          element={<VerifyEmail />}
        />

        {/* Route for phone verification */}
        <Route path="/verify-phone" element={<VerifyPhone />} />

        {/* Route for email change verification */}
        <Route
          path="/verify-email-change/:token"
          element={<VerifyEmailChange />}
        />

        {/* Route for simulation landing pages */}
        {/* Email simulation landing page route - short code format: /sim-email/e{18-char-uuid} */}
        <Route path="/sim-email/:shortCode" element={<SimulationLanding />} />

        {/* SMS simulation landing page route - short code format: /sim-sms/m{18-char-uuid} */}
        <Route path="/sim-sms/:shortCode" element={<SimulationLandingSMS />} />

        {/* Route for password reset */}
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Route for new user password setup */}
        <Route path="/set-password" element={<SetPasswordPage />} />

        {/* Routes for legal documents and blogs */}
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route
          path="/user-consent-agreement"
          element={<UserConsentAgreement />}
        />
        <Route path="/disclosures" element={<Disclosures />} />
        <Route path="/sms-consent" element={<SmsConsent />} />

        {/* Blog routes */}
        <Route path="/blogs" element={<MoreBlogs />} />
        <Route path="/blogs/:shortTitle" element={<Blogs />} />

        {/* Demo registration page (public, no auth required) */}
        <Route path="/demo/register" element={<DemoRegister />} />

        {/* Public event pages. Order matters: the static /events/host-request
            route must precede the /events/:slug wildcard. */}
        <Route path="/events" element={<EventList />} />
        <Route path="/events/host-request" element={<HostRequest />} />
        <Route path="/events/registration/:token" element={<EventManage />} />
        <Route path="/events/:slug" element={<EventDetail />} />
        <Route path="/events/:slug/register" element={<EventRegister />} />
        <Route path="/events/:slug/confirmed" element={<EventConfirmation />} />
        <Route path="/events/:slug/recap" element={<EventRecap />} />

        {/* Login route - redirect to /app (Application handles login modal) */}
        <Route path="/login" element={<Navigate to="/app" replace />} />

        {/* App routes - authenticated application */}
        <Route
          path="/app/:section?"
          element={<Application handleHomeClick={handleHomeClick} />}
        />

        {/* Main marketing site route */}
        <Route path="/" element={<Main />} />

        {/* Redirect any unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ErrorProvider>
      <LoadingProvider>
        <LanguageProvider>
          <Router>
            <NavigationProvider>
              <UserProvider>
                <SubscriptionWrapper />
              </UserProvider>
            </NavigationProvider>
          </Router>
        </LanguageProvider>
      </LoadingProvider>
    </ErrorProvider>
  );
}

// Wrapper component to access UserContext and pass userUuid to SubscriptionProvider
function SubscriptionWrapper() {
  const { loggedInUser } = useUser();

  return (
    <SubscriptionProvider userUuid={loggedInUser?.uuid}>
      <EditionProvider>
        <AccountSetupProvider>
          <AppInner />
        </AccountSetupProvider>
      </EditionProvider>
      <UpdateNotification />
      <Toaster
        containerClassName="toast-stack"
        position="top-center"
        gutter={0}
        toastOptions={{
          duration: 3000,
          style: {
            background: "#363636",
            color: "#fff",
            borderRadius: "8px",
            padding: "12px 20px",
            fontSize: "14px",
            fontWeight: "500",
          },
          success: {
            iconTheme: {
              primary: "#10b981",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      >
        {(t) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              background:
                t.type === "error"
                  ? "#dc2626"
                  : t.type === "success"
                    ? "#363636"
                    : "#363636",
              color: "#fff",
              borderRadius: "8px",
              padding: "12px 16px",
              fontSize: "14px",
              fontWeight: "500",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              maxWidth: "500px",
            }}
          >
            {t.type === "success" && (
              <span style={{ color: "#10b981", fontSize: "18px" }}>✓</span>
            )}
            {t.type === "error" && <span style={{ fontSize: "18px" }}>⚠</span>}
            <span style={{ flex: 1 }}>
              {typeof t.message === "function" ? t.message(t) : t.message}
            </span>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                padding: "4px",
                marginLeft: "8px",
                fontSize: "16px",
                lineHeight: 1,
                opacity: 0.7,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.opacity = 1)}
              onMouseLeave={(e) => (e.target.style.opacity = 0.7)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}
      </Toaster>
    </SubscriptionProvider>
  );
}

export default App;
